import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text, Paragraph } = Typography;

// Mirrors the backend DirectDebitSettings.MaxCancellationNoticeLength cap.
export const MAX_NOTICE_LENGTH = 1000;

export interface DirectDebitNoticeSectionValue {
    cancellationNotice: string;
}

export const DEFAULT_DIRECT_DEBIT_NOTICE: DirectDebitNoticeSectionValue = {
    cancellationNotice: "",
};

// Controlled direct-debit section: the club-specific cancellation notice a member
// reads and must agree to when setting up their mandate. Free text — empty clears
// it (no notice shown on the mandate form).
export const DirectDebitNoticeSection: React.FC<SectionPanelProps<DirectDebitNoticeSectionValue>> = ({
    value,
    onChange,
    readOnly,
}) => (
    <Form layout="vertical" data-testid="settings-direct-debit-form" disabled={readOnly}>
        <Form.Item
            label="Cancellation notice"
            help="Shown next to a tick box the member must accept when setting up their direct debit. Describe your cancellation terms (e.g. one month's notice, or a freeze fee). Leave blank to show no notice."
        >
            <Input.TextArea
                data-testid="settings-direct-debit-notice"
                value={value.cancellationNotice}
                onChange={(e) => onChange({ ...value, cancellationNotice: e.target.value })}
                autoSize={{ minRows: 3, maxRows: 8 }}
                maxLength={MAX_NOTICE_LENGTH}
                showCount
                placeholder="Cancelling your membership requires one month's notice."
            />
        </Form.Item>
    </Form>
);

// Adapter that mounts DirectDebitNoticeSection inside the Settings shell: loads
// current direct-debit config from /config, tracks dirty state, and saves via PUT.
export const DirectDebitNoticeSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<DirectDebitNoticeSectionValue | null>(null);
    const [value, setValue] = useState<DirectDebitNoticeSectionValue>(DEFAULT_DIRECT_DEBIT_NOTICE);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                const loaded: DirectDebitNoticeSectionValue = {
                    cancellationNotice:
                        config?.settings.directDebit?.cancellationNotice
                        ?? DEFAULT_DIRECT_DEBIT_NOTICE.cancellationNotice,
                };
                setInitial(loaded);
                setValue(loaded);
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);
    const valid = value.cancellationNotice.length <= MAX_NOTICE_LENGTH;

    const handleSave = async () => {
        if (!Number.isFinite(clientId) || !valid) return;
        setSaving(true);
        setSaveError(null);
        try {
            const trimmed = value.cancellationNotice.trim();
            await service.updateDirectDebit(clientId, { cancellationNotice: trimmed });
            const saved = { cancellationNotice: trimmed };
            setInitial(saved);
            setValue(saved);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-direct-debit-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                The cancellation terms a member agrees to when setting up direct debit.
            </Paragraph>
            <DirectDebitNoticeSection value={value} onChange={setValue} />
            {saveError && <Alert type="error" message={saveError} data-testid="settings-direct-debit-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-direct-debit-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-direct-debit-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
